<template>
  <div class="connectivity-status" v-if="showStatus && !isConnected">
    <div class="status-bar status-disconnected">
      <div class="status-content">
        <i class="pi pi-exclamation-triangle" />
        <span class="status-text">{{ statusMessage }}</span>
        <Button 
          label="Retry" 
          icon="pi pi-refresh" 
          @click="checkConnectivity"
          :loading="checking"
          class="p-button-sm p-button-outlined retry-btn"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import Button from 'primevue/button'

const isConnected = ref(true)
const checking = ref(false)
const showStatus = ref(false)
const lastCheck = ref<Date | null>(null)

const statusMessage = computed(() => {
  if (checking.value) return 'Checking API connectivity...'
  return 'API Connection Failed - Some features may not work properly'
})

const checkConnectivity = async () => {
  checking.value = true
  try {
    // Test a simple API endpoint with AbortController for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)
    
    const response = await fetch('/api/csrf/', {
      method: 'GET',
      credentials: 'include',
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    isConnected.value = response.ok
    lastCheck.value = new Date()
    
    // Only show status when disconnected
    showStatus.value = !isConnected.value
    
  } catch (error) {
    console.error('Connectivity check failed:', error)
    isConnected.value = false
    showStatus.value = true
  } finally {
    checking.value = false
  }
}

// Periodic connectivity checks
let checkInterval: number | null = null

const startPeriodicChecks = () => {
  // Check every 30 seconds
  checkInterval = window.setInterval(async () => {
    const wasConnected = isConnected.value
    await checkConnectivity()
    
    // Only show status if we became disconnected
    if (wasConnected && !isConnected.value) {
      showStatus.value = true
    }
  }, 30000)
}

const stopPeriodicChecks = () => {
  if (checkInterval) {
    clearInterval(checkInterval)
    checkInterval = null
  }
}

onMounted(() => {
  // Initial connectivity check
  checkConnectivity()
  startPeriodicChecks()
})

onUnmounted(() => {
  stopPeriodicChecks()
})
</script>

<style scoped>
.connectivity-status {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}

.status-bar {
  padding: 0.75rem 1rem;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
}

.status-connected {
  background: linear-gradient(90deg, #10b981, #059669);
  border-bottom: 2px solid #047857;
}

.status-disconnected {
  background: linear-gradient(90deg, #ef4444, #dc2626);
  border-bottom: 2px solid #b91c1c;
  animation: pulse-error 2s infinite;
}

.status-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

.status-text {
  flex: 1;
  text-align: center;
}

.retry-btn {
  margin-left: 1rem;
}

.retry-btn.p-button-outlined {
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.retry-btn.p-button-outlined:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: white;
}

@keyframes pulse-error {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

@media (max-width: 768px) {
  .status-content {
    flex-direction: column;
    gap: 0.25rem;
    text-align: center;
  }
  
  .retry-btn {
    margin-left: 0;
    margin-top: 0.25rem;
  }
}
</style>
